"use client"

import { useState, useEffect } from "react"
import { useSession } from "next-auth/react"
import { useTranslation } from "../../hooks/useTranslation"
import { setUsername } from "../../actions/user"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Spinner } from "../../components/loading-spinner"
import { Check, User, Mail, Heart, Edit3 } from "lucide-react"

interface ProfileProps {
  user: {
    email?: string;
    username?: string;
    id?: string;
    favClub?: {
      id: string | number;
      name: string;
    };
  };
}

export default function Profile({ user }: ProfileProps) {
  const { data: session, update } = useSession()
  const { t } = useTranslation()
  const [usernameValue, setUsernameValue] = useState<string>("")
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Initialize username from session
  useEffect(() => {
    if (session?.user?.username) {
      setUsernameValue(session.user.username)
    }
  }, [session?.user?.username])

  const handleSave = async () => {
    // Validate username
    if (!usernameValue || usernameValue.trim().length < 4) {
      setError(t('profile.usernameError'))
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(false)
    
    try {
      const result = await setUsername(usernameValue.trim())
      if (!result.success) {
        throw new Error(result.error || "Failed to save username")
      }

      // Update session to reflect the changes
      await update({ username: usernameValue.trim() })
      
      setSuccess(true)
      setIsEditing(false)
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000)
    } catch (e) {
      console.error(e)
      setError(e instanceof Error ? e.message : "An error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setUsernameValue(session?.user?.username || "")
    setIsEditing(false)
    setError(null)
  }

  // Get user info from session
  const fullName = session?.user?.name || "-"
  const email = session?.user?.email || user?.email || "-"
  const favoriteClub = session?.user?.favClub?.name || user?.favClub?.name || "-"
  const currentUsername = session?.user?.username || user?.username || "-"

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-[var(--bet-red)] text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-xl">
              <User className="h-5 w-5" />
              {t('profile.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Username - Editable */}
            <div className="space-y-2">
              <Label htmlFor="username" className="flex items-center gap-2 text-gray-700 font-medium">
                <Edit3 className="h-4 w-4" />
                {t('profile.username')}
              </Label>
              {isEditing ? (
                <div className="space-y-3">
                  <Input
                    id="username"
                    type="text"
                    value={usernameValue}
                    onChange={(e) => setUsernameValue(e.target.value)}
                    minLength={4}
                    maxLength={32}
                    className="w-full"
                    placeholder={t('profileModal.usernamePlaceholder')}
                  />
                  <p className="text-xs text-gray-500">{t('profileModal.usernameHint')}</p>
                  
                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleSave} 
                      disabled={!usernameValue || usernameValue.trim().length < 4 || isLoading}
                      className="bg-[var(--bet-red)] hover:bg-[var(--bet-red)]/90"
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <Spinner size={16} className="text-white" />
                          {t('profile.saving')}
                        </span>
                      ) : (
                        t('profile.save')
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      {t('profile.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border">
                  <span className="text-gray-900">{currentUsername}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setIsEditing(true)}
                    className="text-[var(--bet-red)] hover:text-[var(--bet-red)]/80 hover:bg-[var(--bet-red)]/10"
                  >
                    {t('profile.edit')}
                  </Button>
                </div>
              )}
              {success && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="h-4 w-4" />
                  {t('profile.saveSuccess')}
                </p>
              )}
            </div>

            {/* Full Name - Read Only */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-gray-700 font-medium">
                <User className="h-4 w-4" />
                {t('profile.fullName')}
              </Label>
              <div className="flex items-center justify-between p-3 bg-gray-100 rounded-md border border-gray-200">
                <span className="text-gray-700">{fullName}</span>
                <span className="text-xs text-gray-500">{t('profile.readOnly')}</span>
              </div>
            </div>

            {/* Email - Read Only */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-gray-700 font-medium">
                <Mail className="h-4 w-4" />
                {t('profile.email')}
              </Label>
              <div className="flex items-center justify-between p-3 bg-gray-100 rounded-md border border-gray-200">
                <span className="text-gray-700">{email}</span>
                <span className="text-xs text-gray-500">{t('profile.readOnly')}</span>
              </div>
            </div>

            {/* Favorite Club - Read Only */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-gray-700 font-medium">
                <Heart className="h-4 w-4" />
                {t('profile.favoriteClub')}
              </Label>
              <div className="flex items-center justify-between p-3 bg-gray-100 rounded-md border border-gray-200">
                <span className="text-gray-700">{favoriteClub}</span>
                <span className="text-xs text-gray-500">{t('profile.readOnly')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}


